// components/StationAccordion.js
import React, { Component } from 'react';
import { toast } from 'react-toastify';
import StationService from '../services/station_service';
import RevisionHistoryService from '../services/rev_history_service';
import StepThree from './step_three';
import CreateStationVersionModal from './create_station_version_modal';


class StationAccordion extends Component {
    state = {
        versions: {},
        selectedVersion: {},
        stationRecords: {},
        loadingRecords: {},
        refreshingGeneratedFiles: {},
        versionObjects: {},
        versionsLoading: {},
        versionsError: {},
        stationDetailsLoading: {},
        revisionPreviews: {},
        revisionPreviewLoading: {},
        revisionPreviewErrors: {},
        generationVersionByStation: {},
        srProofConsultantByStation: {},
        activeStation: null,
        searchTerm: "",
        pendingOutputUpload: null,
        pendingInputUpload: null,
        inputsPath: localStorage.getItem("inputsPath") || "",
        loading: false,
        format: '',
        generateModalStation: null,
        generationInProgress: false,
        generationCompleted: false,
        openingFileKey: null,
        downloadingFileKey: null,
        downloadingZipKey: null,
        showCreateVersionModal: false,
        currentCreateVersionStation: null,
        newVersion: '',
    };

    componentDidUpdate(prevProps) {
        // When stations are first loaded (from [] → real data)
        if (prevProps.parsedStations.length === 0 && this.props.parsedStations.length > 0) {
            this.props.parsedStations.forEach(station => {
                this.loadVersions(station.name);
            });
        }
    }

    toggleAccordion = (stationName) => {
        const newActive = this.state.activeStation === stationName ? null : stationName;
        this.setState(prev => ({
            activeStation: newActive,
            stationDetailsLoading: newActive
                ? { ...prev.stationDetailsLoading, [stationName]: true }
                : prev.stationDetailsLoading
        }), () => {
            if (!newActive) return;

            this.loadStationDetails(stationName).finally(() => {
                this.setState(prev => ({
                    stationDetailsLoading: {
                        ...prev.stationDetailsLoading,
                        [stationName]: false
                    }
                }));
            });
        });
    };


    handleToggle = (stationName) => {
        this.toggleAccordion(stationName);
    };

    loadStationDetails = async (stationName) => {
        const recordsLoaded = Object.prototype.hasOwnProperty.call(this.state.stationRecords, stationName)
            && this.state.stationRecords[stationName] !== null;
        const { versionStrings, versionObjects } = await this.loadVersions(stationName);
        const selectedVersion = versionStrings[0];
        const selectedVersionObject = versionObjects.find(item => item.version === selectedVersion);

        if (selectedVersionObject) {
            await this.loadRevisionHistoryPreview(stationName, selectedVersionObject.id);
        }

        if (!recordsLoaded && selectedVersion) {
            await this.fetchVerificationRecords(stationName, selectedVersion);
        }
    };

    loadRevisionHistoryPreview = async (stationName, versionId) => {
        if (!versionId) return;

        const previewKey = `${stationName}:${versionId}`;
        this.setState(prev => ({
            revisionPreviewLoading: {
                ...prev.revisionPreviewLoading,
                [previewKey]: true
            },
            revisionPreviewErrors: {
                ...prev.revisionPreviewErrors,
                [previewKey]: null
            }
        }));

        try {
            const [tagDataResponse, tocResponse] = await Promise.all([
                RevisionHistoryService.getRevisionHistory(versionId, "tag_data_pdf"),
                RevisionHistoryService.getRevisionHistory(versionId, "toc_pdf")
            ]);
            const revisionsById = new Map([
                ...(tagDataResponse.data?.revision_history || []),
                ...(tocResponse.data?.revision_history || [])
            ].map(revision => [revision.id, revision]));
            const revisions = [...revisionsById.values()].sort(
                (first, second) => Date.parse(second.created_at || "") - Date.parse(first.created_at || "")
            );
            const revisionsByDocumentType = {
                tag_data_pdf: revisions.filter(revision => revision.document_type === "tag_data_pdf"),
                toc_pdf: revisions.filter(revision => revision.document_type === "toc_pdf")
            };

            this.setState(prev => ({
                revisionPreviews: {
                    ...prev.revisionPreviews,
                    [previewKey]: revisionsByDocumentType
                },
                revisionPreviewLoading: {
                    ...prev.revisionPreviewLoading,
                    [previewKey]: false
                }
            }));
        } catch (error) {
            console.error('Failed to load revision history preview', error);
            this.setState(prev => ({
                revisionPreviewLoading: {
                    ...prev.revisionPreviewLoading,
                    [previewKey]: false
                },
                revisionPreviewErrors: {
                    ...prev.revisionPreviewErrors,
                    [previewKey]: 'Could not load revision history.'
                }
            }));
        }
    };

    loadVersions = async (stationName) => {
        this.setState(prev => ({
            versionsLoading: { ...prev.versionsLoading, [stationName]: true },
            versionsError: { ...prev.versionsError, [stationName]: null }
        }));

        try {
            const response = await StationService.getStationVersions(stationName);

            // Extract just the version strings from the array of objects
            const versionObjects = response.data?.versions || [];
            const versionStrings = versionObjects
                .map(v => v.version)
                .sort((a, b) => {
                    // Optional: sort descending (newest first)
                    return b.localeCompare(a, undefined, { numeric: true });
                });

            this.setState(prev => ({
                versions: { ...prev.versions, [stationName]: versionStrings },
                versionObjects: { ...prev.versionObjects, [stationName]: versionObjects },
                selectedVersion: {
                    ...prev.selectedVersion,
                    [stationName]: versionStrings[0] || null
                },
                versionsLoading: { ...prev.versionsLoading, [stationName]: false },
                versionsError: { ...prev.versionsError, [stationName]: null }
            }));
            return { versionStrings, versionObjects };
        } catch (err) {
            console.error('Failed to load versions for', stationName, err);
            toast.error('Could not load versions');
            const message = err.response?.data?.detail || err.response?.data?.message || err.message || 'Could not load versions for this station.';
            this.setState(prev => ({
                versions: { ...prev.versions, [stationName]: [] },
                versionObjects: { ...prev.versionObjects, [stationName]: [] },
                versionsLoading: { ...prev.versionsLoading, [stationName]: false },
                versionsError: { ...prev.versionsError, [stationName]: message }
            }));
            return { versionStrings: [], versionObjects: [] };
        }
    }



    fetchVerificationRecords = async (stationName, versionOverride = null) => {
        const selectedVersion = versionOverride || this.state.selectedVersion[stationName]
            || this.state.versions[stationName]?.[0];

        if (!selectedVersion) return;

        this.setState(prev => ({
            loadingRecords: { ...prev.loadingRecords, [stationName]: true }
        }));

        try {
            const res = await StationService.getVerificationRecords(stationName, selectedVersion);

            this.setState(prev => ({
                stationRecords: { ...prev.stationRecords, [stationName]: res.data || [] },
                loadingRecords: { ...prev.loadingRecords, [stationName]: false }
            }));
        } catch (err) {
            toast.error("Failed to load verification records");
            this.setState(prev => ({
                loadingRecords: { ...prev.loadingRecords, [stationName]: false }
            }));
        }
    };

    getCombinedRecords = (stationName) => {
        const { stationRecords } = this.state;

        const records = Array.isArray(stationRecords[stationName])
            ? stationRecords[stationName]
            : stationRecords[stationName]
                ? [stationRecords[stationName]]
                : [];

        return records.map((record, idx) => ({
            ...record,
            originalIndex: idx
        })).sort((a, b) => {
            if (a.status === 'flagged' && b.status !== 'flagged') return -1;
            if (b.status === 'flagged' && a.status !== 'flagged') return 1;
            const timeA = a.status === 'verified' && a.verification_timestamp ? a.verification_timestamp : a.timestamp;
            const timeB = b.status === 'verified' && b.verification_timestamp ? b.verification_timestamp : b.timestamp;
            return new Date(timeB) - new Date(timeA);
        });
    };


    getVerificationStatus = (stationName) => {
        const { stationRecords, selectedVersion, versions } = this.state;

        const currentVersion = selectedVersion[stationName] || versions[stationName]?.[0];

        // If no version selected yet → not verified
        if (!currentVersion) {
            return { text: 'Not verified', className: 'bg-secondary' };
        }

        const records = stationRecords[stationName] || [];
        const recordsForThisVersion = Array.isArray(records)
            ? records.filter(r => r.version === currentVersion)
            : records.version === currentVersion
                ? [records]
                : [];

        const flaggedCount = recordsForThisVersion.filter(r => r.status === 'flagged').length;
        const hasVerification = recordsForThisVersion.some(r => r.status === 'verified' && r.type === 'verified');

        if (flaggedCount > 0) {
            return {
                text: `${flaggedCount} active ${flaggedCount === 1 ? 'issue' : 'issues'}`,
                className: 'bg-danger'
            };
        }

        if (hasVerification) {
            return { text: 'Verified', className: 'bg-success' };
        }

        return { text: 'Not verified', className: 'bg-secondary' };
    };

    getSelectedVersion = (stationName) => {
        return this.state.selectedVersion[stationName] || this.state.versions[stationName]?.[0] || null;
    };

    // Public method called from DashboardScreen after verification
    handleVerificationComplete = (stationName, newVersion = null) => {
        console.log("Refreshing station after verification:", stationName, "newVersion:", newVersion);

        // Step 1: Clear cache + force fresh load
        this.setState(
            prev => ({
                versions: { ...prev.versions, [stationName]: null },
                selectedVersion: { ...prev.selectedVersion, [stationName]: null },
                stationRecords: { ...prev.stationRecords, [stationName]: null },
                loadingRecords: { ...prev.loadingRecords, [stationName]: true }
            }),
            async () => {
                try {
                    // Fetch fresh versions
                    const response = await StationService.getStationVersions(stationName);
                    const versionObjects = response.data?.versions || [];
                    const versionStrings = versionObjects
                        .map(v => v.version)
                        .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

                    // Pick the correct version to select
                    const versionToSelect = newVersion && versionStrings.includes(newVersion)
                        ? newVersion
                        : versionStrings[0] || null;

                    // Step 2: Update versions + select new one
                    this.setState({
                        versions: { ...this.state.versions, [stationName]: versionStrings },
                        versionObjects: { ...this.state.versionObjects, [stationName]: versionObjects },
                        selectedVersion: { ...this.state.selectedVersion, [stationName]: versionToSelect }
                    });

                    const selectedVersionObject = versionObjects.find(
                        item => item.version === versionToSelect
                    );
                    if (selectedVersionObject) {
                        await this.loadRevisionHistoryPreview(stationName, selectedVersionObject.id);
                    }

                    // Step 3: Now safely fetch records using the NEW selected version
                    if (versionToSelect) {
                        const res = await StationService.getVerificationRecords(stationName, versionToSelect);

                        this.setState(prev => ({
                            stationRecords: { ...prev.stationRecords, [stationName]: res.data || [] },
                            loadingRecords: { ...prev.loadingRecords, [stationName]: false }
                        }));
                    }

                } catch (err) {
                    console.error("Failed to refresh after verification", err);
                    toast.error("Failed to update verification records");
                    this.setState(prev => ({
                        loadingRecords: { ...prev.loadingRecords, [stationName]: false }
                    }));
                }
            }
        );
    };

    openCreateVersionModal = (stationName) => {
        this.setState({
            showCreateVersionModal: true,
            currentCreateVersionStation: stationName,
            newVersion: ''
        });
    };

    closeCreateVersionModal = () => {
        this.setState({
            showCreateVersionModal: false,
            currentCreateVersionStation: null,
            newVersion: ''
        });
    };

    handleCreateVersion = async () => {
        const { currentCreateVersionStation, newVersion } = this.state;

        if (!/^\d+\.\d+\.\d+$/.test(newVersion)) {
            toast.error('Version must use the format major.minor.patch.');
            return;
        }

        try {
            await StationService.createStationVersion(currentCreateVersionStation, newVersion);
            toast.success(`Version ${newVersion} created successfully`);
            this.closeCreateVersionModal();
            await this.handleVerificationComplete(currentCreateVersionStation, newVersion);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create version');
        }
    };

    handleInputFileChange = (e, stationName) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const expectedFileName = `Station_Info_${stationName}.xlsx`;

        if (file.name !== expectedFileName) {
            toast.error(`Expected file: ${expectedFileName}`);
            e.target.value = "";
            this.setState({ pendingInputUpload: null });
            return;
        }

        this.setState({ pendingInputUpload: null });
        this.props.handleAutoUpload(e, stationName);
        e.target.value = "";
    };

    handleOutputFileChange = (e, stationName) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const pending = this.state.pendingOutputUpload;

        // safety fallback
        if (!pending || pending.stationName !== stationName) {
            this.props.handleOutputUpload(e, stationName);
            e.target.value = "";
            return;
        }

        const { expectedFileName } = pending;
        const isRfid = expectedFileName === "RFID_TIN_layout.png";
        let finalFile = file;

        if (isRfid) {
            const lower = file.name.toLowerCase();
            const validNames = ["rfid_tin_layout.png", "rfid_tin_layout.bmp"];

            if (!validNames.includes(lower)) {
                toast.error(`Expected file: ${expectedFileName}`);
                e.target.value = "";
                this.setState({ pendingOutputUpload: null });
                return;
            }

            // rename .bmp → .png before upload
            if (lower.endsWith(".bmp")) {
                finalFile = new File([file], "RFID_TIN_layout.png", {
                    type: "image/png",
                });
            }
        } else {
            // strict name match for the three xlsx files
            if (file.name !== expectedFileName) {
                toast.error(`Expected file: ${expectedFileName}`);
                e.target.value = "";
                this.setState({ pendingOutputUpload: null });
                return;
            }
        }

        // put the (possibly renamed) file back into the input
        const dt = new DataTransfer();
        dt.items.add(finalFile);
        e.target.files = dt.files;

        this.setState({ pendingOutputUpload: null });
        this.props.handleOutputUpload(e, stationName);
        e.target.value = "";
    };

    setFormat = (value) => {
        this.setState({ format: value });
    };

    getFileLoadingKey = (folderType, station, subfolder, filename) =>
        `${folderType}_${station}_${subfolder || "root"}_${filename}`;

    handleViewFile = async (folderType, station, subfolder, filename) => {
        const fileKey = this.getFileLoadingKey(folderType, station, subfolder, filename);
        this.setState({ openingFileKey: fileKey });
        try {
            await this.props.handleFile("view", folderType, station, subfolder, filename);
        } finally {
            this.setState(prev => prev.openingFileKey === fileKey ? { openingFileKey: null } : null);
        }
    };

    handleDownloadFile = async (folderType, station, subfolder, filename) => {
        const fileKey = this.getFileLoadingKey(folderType, station, subfolder, filename);
        this.setState({ downloadingFileKey: fileKey });
        try {
            await this.props.handleFile("download", folderType, station, subfolder, filename);
        } finally {
            this.setState(prev => prev.downloadingFileKey === fileKey ? { downloadingFileKey: null } : null);
        }
    };

    handleDownloadZip = async (folderType, station, subfolder) => {
        const zipKey = `${Array.isArray(folderType) ? folderType.join("_") : folderType}_${station}_${subfolder || "root"}`;
        this.setState({ downloadingZipKey: zipKey });
        try {
            await this.props.downloadZip(folderType, station, subfolder);
        } finally {
            this.setState(prev => prev.downloadingZipKey === zipKey ? { downloadingZipKey: null } : null);
        }
    };

    getMissingGenerationDocuments = (stationName, version) => {
        const station = this.props.parsedStations.find(item => item.name === stationName);
        const requiredDocuments = [
            { root: 'Input_Documents', fileName: `Station_Info_${stationName}.xlsx` },
            { root: 'Output_Documents', folder: 'tables', fileName: `TOC_${stationName}.xlsx` },
            { root: 'Output_Documents', folder: 'tables', fileName: `TD_${stationName}.xlsx` },
            { root: 'Output_Documents', folder: 'tables', fileName: `RCT_${stationName}.xlsx` },
            { root: 'Output_Documents', folder: 'schematic_plan', fileName: 'RFID_TIN_layout.png' },
        ];

        if (!station || !version) return requiredDocuments.map(document => document.fileName);

        return requiredDocuments
            .filter(document => {
                const filePaths = document.root === 'Input_Documents'
                    ? station.inputFiles
                    : station.outputDocumentFiles || [];
                const folderPath = document.folder ? `${document.folder}/` : '';
                const expectedPath = `/${document.root}/${stationName}/v${version}/${folderPath}${document.fileName}`;

                return !filePaths.some(filePath => filePath.replace(/\\/g, '/').endsWith(expectedPath));
            })
            .map(document => document.fileName);
    };

    getLatestVersionById = (stationName) => {
        const versions = this.state.versionObjects[stationName] || [];

        return versions
            .filter(item => typeof item.version === 'string' && item.version.trim())
            .sort((a, b) => b.id - a.id)[0]?.version || '';
    };

    handleGenerateClick = (stationName, version) => {
        const latestVersion = version || this.getLatestVersionById(stationName);

        // Block instead of silently skipping the check
        if (
            this.state.versionsLoading[stationName]
            || this.state.versionsError[stationName]
            || !latestVersion
        ) {
            toast.error('Version details are not available yet. Please try again.');
            return;
        }

        const missingDocuments = this.getMissingGenerationDocuments(stationName, latestVersion);
        if (missingDocuments.length > 0) {
            toast.error(`Upload all 5 required documents before generating. Missing: ${missingDocuments.join(', ')}`);
            return;
        }

        this.setState(prev => {
            const generationVersionByStation = { ...prev.generationVersionByStation };
            delete generationVersionByStation[stationName];
            return {
                generateModalStation: stationName,
                generationInProgress: false,
                generationCompleted: false,
                generationVersionByStation,
            };
        });
    };

    setGenerationVersion = (stationName, version) => {
        this.setState(prev => ({
            generationVersionByStation: {
                ...prev.generationVersionByStation,
                [stationName]: version
            }
        }));
    };

    setSrProofConsultant = (stationName, consultant) => {
        this.setState(prev => ({
            srProofConsultantByStation: {
                ...prev.srProofConsultantByStation,
                [stationName]: consultant
            }
        }));
    };

    handleGenerationStateChange = (isGenerating, generationSucceeded = false) => {
        this.setState({
            generationInProgress: isGenerating,
            generationCompleted: !isGenerating && generationSucceeded,
        }, () => {
            if (!isGenerating && generationSucceeded) {
                this.handleCloseGenerateModal();
            }
        });
    };

    handleCloseGenerateModal = (shouldRefresh = false) => {
        if (this.state.generationInProgress) return;

        const stationName = this.state.generateModalStation;
        const shouldRefreshStations = this.state.generationCompleted || shouldRefresh;
        this.setState(prev => ({
            generateModalStation: null,
            generationCompleted: false,
            refreshingGeneratedFiles: shouldRefreshStations
                ? { ...prev.refreshingGeneratedFiles, [stationName]: true }
                : prev.refreshingGeneratedFiles,
        }), async () => {
            if (!shouldRefreshStations) return;

            if (this.state.generationCompleted) {
                toast.success("Documents generated successfully");
            }

            try {
                await this.props.refreshStations?.();
            } finally {
                this.setState(prev => ({
                    refreshingGeneratedFiles: { ...prev.refreshingGeneratedFiles, [stationName]: false }
                }));
            }
        });
    };


    render() {
        const {
            parsedStations,
            openVerifyModal,
            openFlagModal,
            openIssueVerifyModal,
            openRevisionHistoryModal,
            fileInputRefs,
            outputFileInputRefs,
            handleAutoUpload,
            handleOutputUpload,
            uploadingStation,
            uploadingOutputDoc,
            deletingDoc,
            formatTimestamp,
            handleFile,
            deleteFile,
        } = this.props;
        const {
            activeStation,
            inputsPath,
            format,
            loading,
            generationInProgress,
            refreshingGeneratedFiles,
        } = this.state;
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const isAdminOrSuperAdmin = Number(user.role) === 0 || Number(user.role) === 1;
        const generateStation = parsedStations.find(station => station.name === this.state.generateModalStation);
        const generationVersions = generateStation
            ? this.state.generationVersionByStation[generateStation.name] || {}
            : {};
        const generateStationVersion = generateStation
            ? generationVersions.pdf
            || this.state.selectedVersion[generateStation.name]
            || this.state.versions[generateStation.name]?.[0]
            || ""
            : "";
        const generatedFileNames = generateStation?.outputFiles
            ? {
                toc: (generateStation.outputFiles || [])
                    .filter(filePath => filePath.includes(`/v${generationVersions.toc || generateStationVersion}/`))
                    .filter(filePath => /TOC/i.test(filePath))
                    .map(filePath => filePath.split('/').pop()),
                excel: (generateStation.outputFiles || [])
                    .filter(filePath => filePath.includes(`/v${generationVersions.excel || generateStationVersion}/`))
                    .filter(filePath => /TD/i.test(filePath) && !/TOC/i.test(filePath) && /\.xlsx$/i.test(filePath))
                    .map(filePath => filePath.split('/').pop()),
                pdf: (generateStation.outputFiles || [])
                    .filter(filePath => filePath.includes(`/v${generationVersions.pdf || generateStationVersion}/`))
                    .filter(filePath => /TD/i.test(filePath) && /\.pdf$/i.test(filePath))
                    .map(filePath => filePath.split('/').pop()),
                rfid: (generateStation.outputFiles || [])
                    .filter(filePath => filePath.includes(`/v${generationVersions.rfid || generateStationVersion}/`))
                    .filter(filePath => /RFID|layout/i.test(filePath))
                    .map(filePath => filePath.split('/').pop()),
                inputs: []
            }
            : { toc: [], excel: [], pdf: [], rfid: [], inputs: [] };

        return (
            <div className="accordion" id="stationAccordion">

                {parsedStations.map((st, i) => {
                    const isActive = activeStation === st.name;
                    const isStationDetailsLoading = Boolean(this.state.stationDetailsLoading[st.name]);
                    const verificationStatus = this.getVerificationStatus(st.name);
                    const stationVersions = this.state.versions[st.name] || [];
                    const latestVersion = stationVersions[0] || "";
                    const selectedVersion = this.state.selectedVersion[st.name] || latestVersion;
                    const selectedVersionObject = (this.state.versionObjects?.[st.name] || [])
                        .find(item => item.version === selectedVersion);
                    const revisionPreviewKey = selectedVersionObject
                        ? `${st.name}:${selectedVersionObject.id}`
                        : "";
                    const revisionPreview = this.state.revisionPreviews[revisionPreviewKey] || {
                        tag_data_pdf: [],
                        toc_pdf: []
                    };
                    const isRevisionPreviewLoading = Boolean(
                        this.state.revisionPreviewLoading[revisionPreviewKey]
                    );
                    const revisionPreviewError = this.state.revisionPreviewErrors[revisionPreviewKey];
                    const isLatestVersion = selectedVersion === latestVersion;
                    const inputZipKey = `Input_Documents_Output_Documents_${st.name}_v${selectedVersion}`;
                    const generatedZipKey = `Converted_Documents_${st.name}_v${selectedVersion}`;
                    const missingGenerationDocuments = this.getMissingGenerationDocuments(st.name, selectedVersion);
                    const generatedVersionRegex = new RegExp(`[/\\\\]v${selectedVersion}([/\\\\]|$)`);
                    const versionedGeneratedFiles = st.outputFiles
                        .filter(filePath => generatedVersionRegex.test(filePath))
                        .filter(filePath => filePath.split('/').pop() !== `v${selectedVersion}`);
                    const hasGeneratedDocuments = versionedGeneratedFiles.length > 0;
                    const isDeletingGeneratedFile = deletingDoc?.startsWith(
                        `${st.name}_Converted_Documents_v${selectedVersion}_`
                    ) || false;
                    const isDeletingStationDocument = deletingDoc?.startsWith(`${st.name}_Input_Documents_`)
                        || deletingDoc?.startsWith(`${st.name}_Output_Documents_`)
                        || false;
                    const outputDocumentFiles = st.outputDocumentFiles || [];
                    const outputVersionRoot = `/Output_Documents/${st.name}/v${selectedVersion}/`;
                    const versionedOutputDocumentFiles = outputDocumentFiles.flatMap((filePath) => {
                        const normalizedPath = filePath.replace(/\\/g, "/");
                        const versionRootIndex = normalizedPath.lastIndexOf(outputVersionRoot);
                        if (versionRootIndex === -1) return [];

                        const relativePath = normalizedPath.slice(versionRootIndex + outputVersionRoot.length);
                        const pathParts = relativePath.split("/");
                        const fileName = pathParts.pop();
                        if (!fileName || !["tables", "schematic_plan"].includes(pathParts[0])) return [];

                        return [{
                            fileName,
                            folder: pathParts.join("/"),
                        }];
                    });
                    // console.log("Rendering station", st.name, "with versions", stationVersions);

                    return (
                        <div
                            className="accordion-item"
                            key={i}
                            style={{
                                borderRadius: '8px',
                                overflow: 'hidden',
                                marginBottom: '1rem',
                                backgroundColor: isActive ? 'rgba(25, 62, 108, 0.025)' : undefined,
                                borderColor: isActive ? 'rgba(25, 62, 108, 0.6)' : undefined,
                                transition: 'background-color 0.2s ease, border-color 0.2s ease'
                            }}
                        >
                            <h2 className="accordion-header" id={`heading-${i}`}>
                                <button
                                    className={`accordion-button custom-accordion ${!isActive ? 'collapsed' : ''}`}
                                    type="button"
                                    onClick={() => this.handleToggle(st.name)}
                                    style={{
                                        backgroundColor: '#193e6c',
                                        color: 'white',
                                        fontWeight: '500',
                                        fontSize: '1rem',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}
                                >
                                    <div className='d-flex justify-content-between w-100'>
                                        <span>
                                            <i className="bi bi-building me-2"></i>
                                            {st.name}
                                            {isStationDetailsLoading && (
                                                <span
                                                    className="spinner-border spinner-border-sm ms-2"
                                                    role="status"
                                                    aria-label={`Loading ${st.name} details`}
                                                ></span>
                                            )}
                                        </span>
                                        <span>
                                            <span className="me-3">
                                                Zone: {st.zone || "SER"}
                                            </span>
                                            <span className="me-3">
                                                {stationVersions && stationVersions.length > 0
                                                    ? `latest: v${stationVersions[0]}`
                                                    : ""
                                                }
                                            </span>
                                            <span className={`badge ${verificationStatus.className} me-2`}>
                                                {verificationStatus.text}
                                            </span>
                                        </span>
                                    </div>
                                </button>
                            </h2>

                            <div
                                id={`collapse-${i}`}
                                className={`accordion-collapse collapse ${isActive ? 'show' : ''}`}
                                aria-labelledby={`heading-${i}`}
                                data-bs-parent="#stationAccordion"
                                style={isActive ? {
                                    backgroundColor: '#ffffff',
                                    borderTop: '1px solid rgba(25, 62, 108, 0.2)'
                                } : undefined}
                            >
                                <div className="accordion-body">
                                    <div className="mb-4">
                                    <div className="px-3 py-3">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                                                <div className="d-flex align-items-center flex-wrap gap-2">
                                                    <h5 className="m-0" style={{ color: '#193e6c', fontWeight: '500' }}>
                                                        <i className="bi bi-building me-2"></i>
                                                        {st.name}
                                                    </h5>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 flex-wrap">

                                                    <div style={{ minWidth: "140px" }}>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            style={{ borderRadius: '6px', border: "1px solid #83878b", backgroundColor: 'transparent' }}
                                                            value={this.state.selectedVersion[st.name] || stationVersions[0] || ""}

                                                            onChange={(e) => {
                                                                const newVersion = e.target.value;

                                                                this.setState(prev => ({
                                                                    selectedVersion: {
                                                                        ...prev.selectedVersion,
                                                                        [st.name]: newVersion
                                                                    }
                                                                }), () => {
                                                                    this.fetchVerificationRecords(st.name);
                                                                    const versionObject =
                                                                        (this.state.versionObjects?.[st.name] || [])
                                                                            .find(item => item.version === newVersion);
                                                                    if (versionObject) {
                                                                        this.loadRevisionHistoryPreview(st.name, versionObject.id);
                                                                    }
                                                                });
                                                            }}
                                                        >
                                                            {stationVersions.length > 0 ? (
                                                                stationVersions.map((ver, idx) => (
                                                                    <option key={idx} value={ver}>
                                                                        v {ver}
                                                                    </option>
                                                                ))
                                                            ) : (
                                                                <option disabled>No versions</option>
                                                            )}
                                                        </select>
                                                    </div>

                                                    {isLatestVersion && isAdminOrSuperAdmin && (
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => {
                                                                if (versionedGeneratedFiles.length < 5) {
                                                                    toast.error("Generate all documents before creating a new version.");
                                                                    return;
                                                                }
                                                                this.openCreateVersionModal(st.name);
                                                            }}
                                                            style={{
                                                                borderRadius: '6px',
                                                                background: '#193e6c',
                                                                border: 'none',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            <i className="bi bi-plus-circle me-1"></i>
                                                            Create New Version
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-3">
                                            {/* Input & Output Files — exactly same */}
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div>
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <div className="d-flex align-items-center">
                                                                <i className="bi bi-upload me-2" style={{ color: '#193e6c' }}></i>
                                                                <strong style={{ color: '#193e6c' }}>Input Documents</strong>
                                                            </div>
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                disabled={this.state.downloadingZipKey === inputZipKey}
                                                                style={{
                                                                    background: 'transparent',
                                                                    color: '#193e6c',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #d0d9e8'
                                                                }}
                                                                onClick={() => this.handleDownloadZip(["Input_Documents", "Output_Documents"], st.name, `v${selectedVersion}`)}
                                                                title="Download input documents as ZIP"
                                                            >
                                                                {this.state.downloadingZipKey === inputZipKey ? (
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-label="Downloading ZIP"></span>
                                                                ) : (
                                                                    <i className="bi bi-download me-1"></i>
                                                                )}
                                                                Download ZIP
                                                            </button>
                                                        </div>

                                                        {(() => {
                                                            const selectedVersion = this.state.selectedVersion[st.name] || stationVersions[0] || "";
                                                            const versionRegex = new RegExp(`[/\\\\]v${selectedVersion}([/\\\\]|$)`);
                                                            const versionedInputFiles = st.inputFiles
                                                                .filter(f => versionRegex.test(f))
                                                                .filter(f => {
                                                                    const last = f.split('/').pop();
                                                                    if (last === `v${selectedVersion}`) return false;
                                                                    return true;
                                                                });

                                                            const expectedFileName = `Station_Info_${st.name}.xlsx`;
                                                            const existingExpected = versionedInputFiles.find(
                                                                f => f.split('/').pop() === expectedFileName
                                                            );
                                                            const isExpectedPresent = !!existingExpected;

                                                            // Extra Input files
                                                            const extraInputFiles = versionedInputFiles.filter(
                                                                f => f.split('/').pop() !== expectedFileName
                                                            );

                                                            // Required Output files
                                                            const expectedOutputFiles = [
                                                                { folder: "tables", fileName: `TOC_${st.name}.xlsx`, accept: ".xlsx" },
                                                                { folder: "tables", fileName: `TD_${st.name}.xlsx`, accept: ".xlsx" },
                                                                { folder: "tables", fileName: `RCT_${st.name}.xlsx`, accept: ".xlsx" },
                                                                { folder: "schematic_plan", fileName: "RFID_TIN_layout.png", accept: ".png,.bmp" },
                                                            ];

                                                            // Extra Output files
                                                            const extraOutputFiles = versionedOutputDocumentFiles.filter(existing =>
                                                                !expectedOutputFiles.some(exp =>
                                                                    exp.folder === existing.folder && exp.fileName === existing.fileName
                                                                )
                                                            );

                                                            if (isDeletingStationDocument) {
                                                                return (
                                                                    <div
                                                                        className="d-flex flex-column align-items-center justify-content-center gap-2 mb-3"
                                                                        role="status"
                                                                        style={{ minHeight: '180px' }}
                                                                    >
                                                                        <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
                                                                        <span className="small text-muted">Deleting and refreshing documents...</span>
                                                                    </div>
                                                                );
                                                            }

                                                            return (
                                                                <ul className="list-group list-group-flush mb-3" style={{ maxHeight: '240px', overflowY: 'auto', borderTop: '1px solid rgba(25, 62, 108, 0.16)', borderRight: '1px solid rgba(25, 62, 108, 0.16)', borderBottom: '1px solid rgba(25, 62, 108, 0.16)', borderLeft: 'none', borderRadius: '6px', '--bs-list-group-bg': 'transparent', '--bs-list-group-border-color': 'rgba(25, 62, 108, 0.16)' }}>
                                                                    {/* ===== Required Input file ===== */}
                                                                    <li
                                                                        className="list-group-item d-flex align-items-center py-2 px-3"
                                                                        style={{
                                                                            cursor: isExpectedPresent ? 'pointer' : 'default',
                                                                            borderLeft: isExpectedPresent ? '4px solid #193e6c' : '3px solid #ccc',
                                                                            backgroundColor: '#f4f7fc',
                                                                            color: isExpectedPresent ? 'inherit' : '#6c757d',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                        title={expectedFileName}
                                                                        onClick={isExpectedPresent
                                                                            ? () => this.handleViewFile("Input_Documents", st.name, `v${selectedVersion}`, expectedFileName)
                                                                            : undefined
                                                                        }
                                                                        onMouseOver={isExpectedPresent
                                                                            ? (e) => {
                                                                                e.currentTarget.style.backgroundColor = '#e7effb';
                                                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 62, 108, 0.12)';
                                                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                                                            }
                                                                            : undefined
                                                                        }
                                                                        onMouseOut={isExpectedPresent
                                                                            ? (e) => {
                                                                                e.currentTarget.style.backgroundColor = '#f4f7fc';
                                                                                e.currentTarget.style.boxShadow = 'none';
                                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                            }
                                                                            : undefined
                                                                        }
                                                                    >
                                                                        <i className={`bi ${isExpectedPresent ? 'bi-file-earmark-text text-primary' : 'bi-file-earmark text-muted'} me-2`}></i>
                                                                        <span className="text-truncate">
                                                                            {expectedFileName.replace(/\.[^/.]+$/, "")}
                                                                        </span>
                                                                        {this.state.openingFileKey === this.getFileLoadingKey("Input_Documents", st.name, `v${selectedVersion}`, expectedFileName) && (
                                                                            <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" aria-label="Opening file"></span>
                                                                        )}

                                                                        {isExpectedPresent ? (
                                                                            <>
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary ms-auto"
                                                                                    disabled={this.state.downloadingFileKey === this.getFileLoadingKey("Input_Documents", st.name, `v${selectedVersion}`, expectedFileName)}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        this.handleDownloadFile("Input_Documents", st.name, `v${selectedVersion}`, expectedFileName);
                                                                                    }}
                                                                                    style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                >
                                                                                    {this.state.downloadingFileKey === this.getFileLoadingKey("Input_Documents", st.name, `v${selectedVersion}`, expectedFileName) ? (
                                                                                        <span className="spinner-border spinner-border-sm" role="status" aria-label="Downloading file"></span>
                                                                                    ) : (
                                                                                        <i className="bi bi-download"></i>
                                                                                    )}
                                                                                </button>
                                                                                {isLatestVersion && isAdminOrSuperAdmin && (
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-danger ms-2"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            deleteFile("Input_Documents", st.name, `v${selectedVersion}`, expectedFileName);
                                                                                        }}
                                                                                        style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                    >
                                                                                        <i className="bi bi-trash"></i>
                                                                                    </button>
                                                                                )}
                                                                            </>
                                                                        ) : isAdminOrSuperAdmin ? (
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary ms-auto"
                                                                                title={`Upload ${expectedFileName}`}
                                                                                disabled={uploadingStation === st.name}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    this.setState({
                                                                                        pendingInputUpload: {
                                                                                            stationName: st.name,
                                                                                            expectedFileName,
                                                                                        }
                                                                                    }, () => {
                                                                                        fileInputRefs[st.name]?.click();
                                                                                    });
                                                                                }}
                                                                                style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                            >
                                                                                {uploadingStation === st.name ? (
                                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                                ) : (
                                                                                    <i className="bi bi-cloud-arrow-up"></i>
                                                                                )}
                                                                            </button>
                                                                        ) : null}
                                                                    </li>

                                                                    {/* ===== Extra Input files (red + strikethrough) ===== */}
                                                                    {extraInputFiles.map((f, idx) => {
                                                                        const fileName = f.split('/').pop();
                                                                        return (
                                                                            <li
                                                                                key={`extra-input-${idx}`}
                                                                                className="list-group-item d-flex align-items-center py-2 px-3"
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    borderLeft: '4px solid #193e6c',
                                                                                    backgroundColor: '#f4f7fc',
                                                                                    transition: 'all 0.2s ease'
                                                                                }}
                                                                                title={fileName}
                                                                                onClick={() => this.handleViewFile("Input_Documents", st.name, `v${selectedVersion}`, fileName)}
                                                                                onMouseOver={(e) => {
                                                                                    e.currentTarget.style.backgroundColor = '#e7effb';
                                                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 62, 108, 0.12)';
                                                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                                                }}
                                                                                onMouseOut={(e) => {
                                                                                    e.currentTarget.style.backgroundColor = '#f4f7fc';
                                                                                    e.currentTarget.style.boxShadow = 'none';
                                                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                                                }}
                                                                            >
                                                                                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                                                                                <span className="text-truncate" style={{ color: '#dc3545', textDecoration: 'line-through' }}>
                                                                                    {fileName.replace(/\.[^/.]+$/, "")}
                                                                                </span>
                                                                                {this.state.openingFileKey === this.getFileLoadingKey("Input_Documents", st.name, `v${selectedVersion}`, fileName) && (
                                                                                    <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" aria-label="Opening file"></span>
                                                                                )}
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary ms-auto"
                                                                                    disabled={this.state.downloadingFileKey === this.getFileLoadingKey("Input_Documents", st.name, `v${selectedVersion}`, fileName)}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        this.handleDownloadFile("Input_Documents", st.name, `v${selectedVersion}`, fileName);
                                                                                    }}
                                                                                    style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                >
                                                                                    {this.state.downloadingFileKey === this.getFileLoadingKey("Input_Documents", st.name, `v${selectedVersion}`, fileName) ? (
                                                                                        <span className="spinner-border spinner-border-sm" role="status" aria-label="Downloading file"></span>
                                                                                    ) : (
                                                                                        <i className="bi bi-download"></i>
                                                                                    )}
                                                                                </button>
                                                                                {isLatestVersion && isAdminOrSuperAdmin && (
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-danger ms-2"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            deleteFile("Input_Documents", st.name, `v${selectedVersion}`, fileName);
                                                                                        }}
                                                                                        style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                    >
                                                                                        <i className="bi bi-trash"></i>
                                                                                    </button>
                                                                                )}
                                                                            </li>
                                                                        );
                                                                    })}

                                                                    {/* ===== Required Output files ===== */}
                                                                    {expectedOutputFiles.map((expected) => {
                                                                        const existing = versionedOutputDocumentFiles.find(
                                                                            f => f.folder === expected.folder && f.fileName === expected.fileName
                                                                        );
                                                                        const isPresent = !!existing;

                                                                        return (
                                                                            <li
                                                                                key={`${expected.folder}/${expected.fileName}`}
                                                                                className="list-group-item d-flex align-items-center py-2 px-3"
                                                                                style={{
                                                                                    cursor: isPresent ? 'pointer' : 'default',
                                                                                    borderLeft: isPresent ? '4px solid #193e6c' : '3px solid #ccc',
                                                                                    backgroundColor: '#f4f7fc',
                                                                                    color: isPresent ? 'inherit' : '#6c757d',
                                                                                    transition: 'all 0.2s ease'
                                                                                }}
                                                                                title={expected.fileName}
                                                                                onClick={isPresent
                                                                                    ? () => this.handleViewFile("Output_Documents", st.name, `v${selectedVersion}/${expected.folder}`, expected.fileName)
                                                                                    : undefined
                                                                                }
                                                                                onMouseOver={isPresent
                                                                                    ? (e) => {
                                                                                        e.currentTarget.style.backgroundColor = '#e7effb';
                                                                                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 62, 108, 0.12)';
                                                                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                                                                    }
                                                                                    : undefined
                                                                                }
                                                                                onMouseOut={isPresent
                                                                                    ? (e) => {
                                                                                        e.currentTarget.style.backgroundColor = '#f4f7fc';
                                                                                        e.currentTarget.style.boxShadow = 'none';
                                                                                        e.currentTarget.style.transform = 'translateY(0)';
                                                                                    }
                                                                                    : undefined
                                                                                }
                                                                            >
                                                                                <i className={`bi ${isPresent ? 'bi-file-earmark-check text-primary' : 'bi-file-earmark text-muted'} me-2`}></i>
                                                                                <span className="text-truncate">
                                                                                    {expected.fileName.replace(/\.[^/.]+$/, "")}
                                                                                </span>
                                                                                {this.state.openingFileKey === this.getFileLoadingKey("Output_Documents", st.name, `v${selectedVersion}/${expected.folder}`, expected.fileName) && (
                                                                                    <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" aria-label="Opening file"></span>
                                                                                )}

                                                                                {isPresent ? (
                                                                                    <>
                                                                                        <button
                                                                                            className="btn btn-sm btn-outline-primary ms-auto"
                                                                                            title={`Download ${expected.fileName}`}
                                                                                            disabled={this.state.downloadingFileKey === this.getFileLoadingKey("Output_Documents", st.name, `v${selectedVersion}/${expected.folder}`, expected.fileName)}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                this.handleDownloadFile("Output_Documents", st.name, `v${selectedVersion}/${expected.folder}`, expected.fileName);
                                                                                            }}
                                                                                            style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                        >
                                                                                            {this.state.downloadingFileKey === this.getFileLoadingKey("Output_Documents", st.name, `v${selectedVersion}/${expected.folder}`, expected.fileName) ? (
                                                                                                <span className="spinner-border spinner-border-sm" role="status" aria-label="Downloading file"></span>
                                                                                            ) : (
                                                                                                <i className="bi bi-download"></i>
                                                                                            )}
                                                                                        </button>
                                                                                        {isLatestVersion && isAdminOrSuperAdmin && (
                                                                                            <button
                                                                                                className="btn btn-sm btn-outline-danger ms-2"
                                                                                                title={`Delete ${expected.fileName}`}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    deleteFile("Output_Documents", st.name, `v${selectedVersion}/${expected.folder}`, expected.fileName);
                                                                                                }}
                                                                                                style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                            >
                                                                                                <i className="bi bi-trash"></i>
                                                                                            </button>
                                                                                        )}
                                                                                    </>
                                                                                ) : isAdminOrSuperAdmin ? (
                                                                                                <button
                                                                                            className="btn btn-sm btn-outline-primary ms-auto"
                                                                                            title={`Upload ${expected.fileName}`}
                                                                                            disabled={uploadingOutputDoc === st.name}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                this.setState({
                                                                                                    pendingOutputUpload: {
                                                                                                        stationName: st.name,
                                                                                                        folder: expected.folder,
                                                                                                        expectedFileName: expected.fileName,
                                                                                                    }
                                                                                                }, () => {
                                                                                                    const input = outputFileInputRefs?.[st.name];
                                                                                                    if (input) {
                                                                                                        input.accept = expected.accept;
                                                                                                        input.click();
                                                                                                    }
                                                                                                });
                                                                                            }}
                                                                                            style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                        >
                                                                                            {uploadingOutputDoc === st.name ? (
                                                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                                            ) : (
                                                                                                <i className="bi bi-cloud-arrow-up"></i>
                                                                                            )}
                                                                                        </button>
                                                                                ) : null}
                                                                            </li>
                                                                        );
                                                                    })}

                                                                    {/* ===== Extra Output files (red + strikethrough) ===== */}
                                                                    {extraOutputFiles.map((file, idx) => (
                                                                        <li
                                                                            key={`extra-output-${idx}`}
                                                                            className="list-group-item d-flex align-items-center py-2 px-3"
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                borderLeft: '4px solid #28a745',
                                                                                backgroundColor: '#f2faf4',
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                            title={file.fileName}
                                                                            onClick={() => this.handleViewFile("Output_Documents", st.name, `v${selectedVersion}/${file.folder}`, file.fileName)}
                                                                            onMouseOver={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#e5f5e9';
                                                                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 135, 84, 0.12)';
                                                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                                                            }}
                                                                            onMouseOut={(e) => {
                                                                                e.currentTarget.style.backgroundColor = '#f2faf4';
                                                                                e.currentTarget.style.boxShadow = 'none';
                                                                                e.currentTarget.style.transform = 'translateY(0)';
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-file-earmark-check me-2 text-success"></i>
                                                                            <span className="text-truncate" style={{ color: '#dc3545', textDecoration: 'line-through' }}>
                                                                                {file.fileName.replace(/\.[^/.]+$/, "")}
                                                                            </span>
                                                                            {this.state.openingFileKey === this.getFileLoadingKey("Output_Documents", st.name, `v${selectedVersion}/${file.folder}`, file.fileName) && (
                                                                                <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" aria-label="Opening file"></span>
                                                                            )}
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary ms-auto"
                                                                                title={`Download ${file.fileName}`}
                                                                                disabled={this.state.downloadingFileKey === this.getFileLoadingKey("Output_Documents", st.name, `v${selectedVersion}/${file.folder}`, file.fileName)}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    this.handleDownloadFile("Output_Documents", st.name, `v${selectedVersion}/${file.folder}`, file.fileName);
                                                                                }}
                                                                                style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                            >
                                                                                {this.state.downloadingFileKey === this.getFileLoadingKey("Output_Documents", st.name, `v${selectedVersion}/${file.folder}`, file.fileName) ? (
                                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-label="Downloading file"></span>
                                                                                ) : (
                                                                                    <i className="bi bi-download"></i>
                                                                                )}
                                                                            </button>
                                                                            {isAdminOrSuperAdmin && (
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-danger ms-2"
                                                                                    title={`Delete ${file.fileName}`}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        deleteFile("Output_Documents", st.name, `v${selectedVersion}/${file.folder}`, file.fileName);
                                                                                    }}
                                                                                    style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                >
                                                                                    <i className="bi bi-trash"></i>
                                                                                </button>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            );
                                                        })()}

                                                        {isAdminOrSuperAdmin && (
                                                            <>
                                                                <input
                                                                    type="file"
                                                                    ref={(ref) => (fileInputRefs[st.name] = ref)}
                                                                    style={{ display: 'none' }}
                                                                    accept=".xlsx"
                                                                    onChange={(e) => this.handleInputFileChange(e, st.name)}
                                                                />
                                                            <input
                                                                type="file"
                                                                hidden
                                                                accept=".png,.xlsx,.bmp"
                                                                ref={(ref) => {
                                                                    if (outputFileInputRefs) {
                                                                        outputFileInputRefs[st.name] = ref;
                                                                    }
                                                                }}
                                                                onChange={(e) => this.handleOutputFileChange(e, st.name)}
                                                            />
                                                            </>
                                                        )}
                                                    </div>
                                                </div>


                                                <div className="col-md-6">
                                                    <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2'>
                                                        <div className="d-flex align-items-center mb-2">
                                                            <i className="bi bi-download me-2" style={{ color: '#193e6c' }}></i>
                                                            <strong style={{ color: '#193e6c' }}>Generated Documents</strong>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <button
                                                                className="btn btn-outline-primary btn-sm"
                                                                disabled={versionedGeneratedFiles.length === 0 || this.state.downloadingZipKey === generatedZipKey}
                                                                style={{
                                                                    background: 'transparent',
                                                                    color: '#193e6c',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #d0d9e8'
                                                                }}
                                                                onClick={() => this.handleDownloadZip("Converted_Documents", st.name, `v${selectedVersion}`)}
                                                            >
                                                                {this.state.downloadingZipKey === generatedZipKey ? (
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-label="Downloading ZIP"></span>
                                                                ) : (
                                                                    <i className="bi bi-download me-2"></i>
                                                                )}
                                                                Download ZIP
                                                            </button>
                                                        </div>

                                                    </div>
                                                    {refreshingGeneratedFiles[st.name] || isDeletingGeneratedFile ? (
                                                        <div
                                                            className="d-flex flex-column align-items-center justify-content-center gap-2"
                                                            role="status"
                                                            style={{ minHeight: '180px' }}
                                                        >
                                                            <span className="spinner-border text-primary" aria-hidden="true"></span>
                                                            <span className="small text-muted">
                                                                {isDeletingGeneratedFile ? 'Deleting and refreshing generated documents...' : 'Refreshing generated documents...'}
                                                            </span>
                                                        </div>
                                                    ) : (() => {
                                                        return versionedGeneratedFiles.length ? (
                                                            <ul className="list-group list-group-flush" style={{ maxHeight: '260px', overflowY: 'auto', borderTop: '1px solid rgba(40, 167, 69, 0.16)', borderRight: '1px solid rgba(40, 167, 69, 0.16)', borderBottom: '1px solid rgba(40, 167, 69, 0.16)', borderLeft: 'none', borderRadius: '6px', '--bs-list-group-bg': 'transparent', '--bs-list-group-border-color': 'rgba(40, 167, 69, 0.16)' }}>
                                                                {versionedGeneratedFiles.map((f, idx) => (
                                                                    <li
                                                                        key={idx}
                                                                        className="list-group-item d-flex align-items-center py-2 px-3"
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            borderLeft: '4px solid #28a745',
                                                                            backgroundColor: '#f2faf4',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                        onClick={() => this.handleViewFile("Converted_Documents", st.name, `v${selectedVersion}`, f.split('/').pop())}
                                                                        onMouseOver={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#e5f5e9';
                                                                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(25, 135, 84, 0.12)';
                                                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                                                        }}
                                                                        onMouseOut={(e) => {
                                                                            e.currentTarget.style.backgroundColor = '#f2faf4';
                                                                            e.currentTarget.style.boxShadow = 'none';
                                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-file-earmark-check me-2 text-success"></i>
                                                                        <span className="text-truncate">{f.split('/').pop().replace(/\.[^/.]+$/, "")}</span>
                                                                        {this.state.openingFileKey === this.getFileLoadingKey("Converted_Documents", st.name, `v${selectedVersion}`, f.split('/').pop()) && (
                                                                            <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" aria-label="Opening file"></span>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-sm btn-outline-success ms-auto"
                                                                            disabled={this.state.downloadingFileKey === this.getFileLoadingKey("Converted_Documents", st.name, `v${selectedVersion}`, f.split('/').pop())}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                this.handleDownloadFile("Converted_Documents", st.name, `v${selectedVersion}`, f.split('/').pop());
                                                                            }}
                                                                            style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                        >
                                                                            {this.state.downloadingFileKey === this.getFileLoadingKey("Converted_Documents", st.name, `v${selectedVersion}`, f.split('/').pop()) ? (
                                                                                <span className="spinner-border spinner-border-sm" role="status" aria-label="Downloading file"></span>
                                                                            ) : (
                                                                                <i className="bi bi-download"></i>
                                                                            )}
                                                                        </button>
                                                                        {isLatestVersion && isAdminOrSuperAdmin && (
                                                                            <button
                                                                                className="btn btn-sm btn-outline-danger ms-2"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    deleteFile("Converted_Documents", st.name, `v${selectedVersion}`, f.split('/').pop());
                                                                                }}
                                                                                style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                            >
                                                                                <i className="bi bi-trash"></i>
                                                                            </button>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <div
                                                                className="d-flex flex-column align-items-center justify-content-center text-center px-3 py-5"
                                                                role="status"
                                                                style={{
                                                                    minHeight: '180px',
                                                                    backgroundColor: '#f8fafc',
                                                                    border: '1px dashed #cbd5e1',
                                                                    borderRadius: '8px'
                                                                }}
                                                            >
                                                                <i className="bi bi-file-earmark-x text-secondary mb-3" style={{ fontSize: '1.75rem' }}></i>
                                                                <strong className="text-dark mb-1">No generated documents</strong>
                                                                <span className="text-muted small">There are no generated files for this version.</span>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>

                                                {isAdminOrSuperAdmin && (
                                                <div className="row mx-0 align-items-stretch mt-3">
                                                    <div className="col-md-6 ps-0 d-flex">
                                                        <div
                                                            className="h-100 w-100 p-0"
                                                            style={{
                                                                backgroundColor: 'transparent',
                                                                border: '1px solid #659bdd',
                                                                borderLeft: '4px solid #193e6c',
                                                                borderRadius: '8px',
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            {!isLatestVersion ? (
                                                                <div
                                                                    className="d-flex h-100 align-items-center justify-content-center gap-2 px-3 text-muted small"
                                                                    role="status"
                                                                >
                                                                    <i className="bi bi-lock-fill" aria-hidden="true"></i>
                                                                    Revision history is available for the latest version only.
                                                                </div>
                                                            ) : isRevisionPreviewLoading ? (
                                                                <div className="small text-muted h-100 d-flex align-items-center justify-content-center" role="status">
                                                                    <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                                                                    Loading revision history...
                                                                </div>
                                                            ) : revisionPreviewError ? (
                                                                <div className="small text-danger h-100 d-flex align-items-center" role="alert">
                                                                    {revisionPreviewError}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="d-flex align-items-stretch h-100">
                                                                        <div className="d-flex flex-column flex-shrink-0 justify-content-center px-2" style={{ minWidth: '105px', backgroundColor: '#dbdfe6', borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                                                                            <div className="d-flex align-items-center justify-content-center gap-1" style={{ fontWeight: 700, color: '#193e6c', fontSize: '0.9rem' }}>
                                                                                REVISION<br/>HISTORY
                                                                            </div>
                                                                        </div>
                                                                        {[
                                                                            { documentType: 'tag_data_pdf', label: 'Tag Data (TD)' },
                                                                            { documentType: 'toc_pdf', label: 'Table of Control (TOC)' }
                                                                        ].map(({ documentType, label }) => {
                                                                            const latestRevision =
                                                                                revisionPreview[documentType]?.[0] || null;

                                                                            return (
                                                                                <div
                                                                                    className="flex-grow-1 ps-2"
                                                                                    key={documentType}
                                                                                    style={{ minWidth: 0, borderLeft: '1px solid #659bdd' }}
                                                                                >
                                                                                    <div
                                                                                        className="d-flex justify-content-between align-items-center gap-1 rounded-2 px-2 py-1 h-100"
                                                                                        style={{ backgroundColor: 'transparent' }}
                                                                                    >
                                                                                        <div
                                                                                            className="text-truncate"
                                                                                            title={latestRevision
                                                                                                ? `Last updated by ${latestRevision.author || 'Unknown author'}`
                                                                                                : 'Not available'}
                                                                                        >
                                                                                            <div className="fw-semibold text-dark text-truncate">{label}</div>
                                                                                            <div className={latestRevision ? 'text-success text-truncate' : 'text-muted'}  style={{ fontSize: '0.75rem'}}>
                                                                                                {latestRevision
                                                                                                    ? `${latestRevision.author || 'Unknown author'} · ${formatTimestamp(latestRevision.created_at, true)}`
                                                                                                    : 'Not available'}
                                                                                            </div>
                                                                                        </div>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-sm btn-outline-primary flex-shrink-0"
                                                                                            disabled={!selectedVersionObject}
                                                                                            onClick={() => {
                                                                                                if (!selectedVersionObject) {
                                                                                                    toast.error("Version not found");
                                                                                                    return;
                                                                                                }

                                                                                                openRevisionHistoryModal(
                                                                                                    st.name,
                                                                                                    selectedVersionObject.id,
                                                                                                    selectedVersion,
                                                                                                    documentType
                                                                                                );
                                                                                            }}
                                                                                            style={{ whiteSpace: 'nowrap', padding: '2px 6px', fontSize: '0.75rem' }}
                                                                                        >
                                                                                            {latestRevision ? (
                                                                                                <>
                                                                                                    <i className="bi bi-pencil-square me-1" aria-hidden="true"></i>
                                                                                                    Update
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <i className="bi bi-plus-lg me-1" aria-hidden="true"></i>
                                                                                                    Add
                                                                                                </>
                                                                                            )}
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6 pe-0 d-flex">
                                                        {isLatestVersion ? (
                                                            <div
                                                                className="d-flex h-100 w-100 justify-content-between align-items-center gap-2 p-2"
                                                                style={{
                                                                    border: `1px solid ${hasGeneratedDocuments ? '#78e59e' : '#d8e1ec'}`,
                                                                    borderLeft: `4px solid ${hasGeneratedDocuments ? '#198754' : '#193e6c'}`,
                                                                    borderRadius: '8px'
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <i
                                                                        className={`bi ${hasGeneratedDocuments ? 'bi-check-circle-fill text-success' : 'bi-arrow-repeat'}`}
                                                                        style={{ color: hasGeneratedDocuments ? undefined : '#193e6c', fontSize: '1.2rem' }}
                                                                    ></i>
                                                                    <div>
                                                                        <div className="fw-semibold text-dark">{hasGeneratedDocuments ? 'Regenerate station documents' : 'Generate station documents'}</div>
                                                                        <div className={`small fw-semibold mt-1 ${missingGenerationDocuments.length === 0 ? 'text-success' : 'text-secondary'}`}>
                                                                            {5 - missingGenerationDocuments.length} of 5 documents ready in Input Documents
                                                                        </div>
                                                                </div>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary d-inline-flex align-items-center"
                                                                    onClick={() => this.handleGenerateClick(st.name, selectedVersion)}
                                                                    style={{
                                                                        backgroundColor: '#193e6c',
                                                                        borderColor: '#193e6c',
                                                                        borderRadius: '6px',
                                                                        padding: '0.55rem 1rem',
                                                                        fontWeight: 600,
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    <i className="bi bi-play-fill me-2"></i>
                                                                    {hasGeneratedDocuments ? 'Regenerate documents' : 'Generate documents'}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="d-flex h-100 w-100 align-items-center gap-2 p-2"
                                                                role="status"
                                                                style={{
                                                                    backgroundColor: '#f4f7fb',
                                                                    border: '1px solid #d8e1ec',
                                                                    borderLeft: '4px solid #6c757d',
                                                                    borderRadius: '8px'
                                                                }}
                                                            >
                                                                <i className="bi bi-lock-fill text-secondary" aria-hidden="true"></i>
                                                                <div>
                                                                    <div className="fw-semibold text-dark">This version is already completed</div>
                                                                    <div className="small text-muted">Document generation is only available for the latest version.</div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>
                                                )}

                                            </div>

                                            <hr className="mb-3 mt-3" />

                                            {/* Verification Records — 100% unchanged */}
                                            <div className="row align-items-top g-3">
                                                <div className="col-md-7 d-flex align-items-center mb-3">
                                                    <i className="bi bi-clipboard2-check me-2 fs-5" style={{ color: '#193e6c' }}></i>
                                                    <h6 className="mb-0 fw-bold" style={{ color: '#193e6c' }}>Verification & Validation Records</h6>
                                                </div>
                                                <div className="col-md-5 d-flex justify-content-end gap-2">
                                                            <button
                                                                className="btn d-flex align-items-center justify-content-center py-2 px-3"
                                                                style={{
                                                                    background: 'linear-gradient(to bottom, #e6fff5, #a1ffe8)',
                                                                    color: '#056404',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #a1ffe8',
                                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                                    transition: 'all 0.2s',
                                                                    fontWeight: '500',
                                                                    opacity: versionedGeneratedFiles.length < 5 ? 0.65 : 1,
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => {
                                                                    const records = this.getCombinedRecords(st.name);
                                                                    const activeIssues = records.filter(
                                                                        r => r.version === selectedVersion && r.status === 'flagged'
                                                                    );
                                                                    if (activeIssues.length > 0) {
                                                                        toast.warning("Please clear all active issues before verifying the station.");
                                                                        return;
                                                                    }
                                                                    if (versionedGeneratedFiles.length < 5) {
                                                                        toast.error("Generate all documents before verifying the station.");
                                                                        return;
                                                                    }
                                                                    const version = this.getSelectedVersion(st.name);
                                                                    openVerifyModal(st.name, version);
                                                                }}
                                                            >
                                                                <i className="bi bi-check-circle-fill me-2"></i>
                                                                Verify & Validate
                                                            </button>

                                                            {isLatestVersion && (
                                                                <button
                                                                    className="btn d-flex align-items-center justify-content-center py-2 px-3"
                                                                    style={{
                                                                        background: 'linear-gradient(to bottom, #fff9e6, #ffe8a1)',
                                                                        color: '#856404',
                                                                        borderRadius: '8px',
                                                                        border: '1px solid #ffeeba',
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                                        transition: 'all 0.2s',
                                                                        fontWeight: '500',
                                                                        opacity: versionedGeneratedFiles.length === 0 ? 0.65 : 1,
                                                                        cursor: versionedGeneratedFiles.length === 0 ? 'not-allowed' : 'pointer'
                                                                    }}
                                                                    onClick={() => {
                                                                        const version = this.getSelectedVersion(st.name);
                                                                        versionedGeneratedFiles.length > 0 && openFlagModal(st.name, version);
                                                                    }}
                                                                    disabled={versionedGeneratedFiles.length === 0}
                                                                >
                                                                    <i className="bi bi-flag-fill me-2"></i>
                                                                    Raise Flag
                                                                </button>
                                                            )}
                                                </div>

                                                {/* Records Grid */}
                                                <div className="row">
                                                    <div className="col-md-12">
                                                        <h6 className="fw-bold mb-3" style={{ color: "#193e6c" }}>All Records</h6>
                                                        {this.getCombinedRecords(st.name).length > 0 ? (
                                                            <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                                <div className="row row-cols-1 row-cols-md-2 g-3">
                                                                    {this.getCombinedRecords(st.name).map((record, idx) => (
                                                                        <div key={idx} className="col">
                                                                            <div
                                                                                className="card h-100 shadow-sm border-0"
                                                                                style={{
                                                                                    borderRadius: '8px',
                                                                                    background: record.status === 'verified' ? '#e6fff5' : '#fffbe6',
                                                                                    borderLeft: record.status === 'flagged' ? '4px solid #dc3545' : '4px solid #28a745'
                                                                                }}
                                                                            >
                                                                                <div className="card-body py-3">
                                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                                        <span className="fw-bold text-dark">
                                                                                            {record.verified_by || 'Unknown'} <span className='text-muted small'>({formatTimestamp(record.status === 'verified' && record.verification_timestamp ? record.verification_timestamp : record.timestamp, true)})</span>
                                                                                        </span>
                                                                                        <div className="d-flex align-items-center gap-2">
                                                                                            {record.status === 'verified' ? (
                                                                                                <span className="badge bg-success">
                                                                                                    {record.type === 'flagged' ?
                                                                                                        `${record.remarks.length} ${record.remarks.length === 1 ? 'issue' : 'issues'} fixed` :
                                                                                                        'verified'}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <span className="badge bg-danger">
                                                                                                        {record.remarks.length} {record.remarks.length === 1 ? 'issue' : 'issues'}
                                                                                                    </span>
                                                                                                    <button
                                                                                                        className="btn btn-sm btn-success d-flex align-items-center"
                                                                                                        onClick={() => {
                                                                                                            const version = this.getSelectedVersion(st.name);
                                                                                                            openIssueVerifyModal(st.name, record.id, record.verified_by, version);
                                                                                                        }}
                                                                                                        style={{ borderRadius: '6px', padding: '4px 8px' }}
                                                                                                    >
                                                                                                        <i className="bi bi-check-circle-fill me-1"></i>
                                                                                                        Fix
                                                                                                    </button>
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                    {record.remarks && Array.isArray(record.remarks) ? (
                                                                                        record.remarks.map((remark, remarkIdx) => (
                                                                                            <p key={remarkIdx} className="mb-0">{remarkIdx + 1}: {remark || 'No remarks'}</p>
                                                                                        ))
                                                                                    ) : (
                                                                                        <p className="mb-0">{record.remarks || 'No remarks'}</p>
                                                                                    )}
                                                                                    {record.status === 'verified' && record.verification_remarks && (
                                                                                        <div className="mt-2 text-muted small">
                                                                                            Verified on {formatTimestamp(record.verification_timestamp, true)} by {record.verified_by}
                                                                                            <p className="mb-0">Remarks: {record.verification_remarks}</p>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="text-muted">No records available</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })
                }
                {generateStation && (
                    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)", zIndex: 1055 }}>
                        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {generationInProgress ? 'Generating station documents...' : `Generate Documents - ${generateStation.name}`}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        disabled={generationInProgress}
                                        onClick={this.handleCloseGenerateModal}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <StepThree
                                        station={generateStation.name}
                                        stationZone={generateStation.zone || "SER"}
                                        srProofConsultant={this.state.srProofConsultantByStation[generateStation.name] || "A. VINAYAGAM"}
                                        setSrProofConsultant={(consultant) => this.setSrProofConsultant(generateStation.name, consultant)}
                                        inputPath={inputsPath}
                                        outputPath={inputsPath}
                                        selectedVersion={generateStationVersion}
                                        availableVersions={this.state.versionObjects[generateStation.name] || []}
                                        versionsLoading={Boolean(this.state.versionsLoading[generateStation.name])}
                                        versionsError={this.state.versionsError[generateStation.name]}
                                        onGenerationVersionChange={(version) => this.setGenerationVersion(generateStation.name, version)}
                                        generatedFileNames={generatedFileNames}
                                        format={format}
                                        setFormat={this.setFormat}
                                        loading={loading}
                                        setLoading={(val) => this.setState({ loading: val })}
                                        disabled={generateStation.outputFiles.length === 0}
                                        onDownloadGeneratedZip={() => this.handleDownloadZip(
                                            "Converted_Documents",
                                            generateStation.name
                                        )}
                                        isDownloadingGeneratedZip={this.state.downloadingZipKey === `Converted_Documents_${generateStation.name}_root`}
                                        onGenerationStateChange={this.handleGenerationStateChange}
                                        onCloseGenerationModal={this.handleCloseGenerateModal}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <CreateStationVersionModal
                    show={this.state.showCreateVersionModal}
                    station={this.state.currentCreateVersionStation}
                    version={this.state.newVersion}
                    onClose={this.closeCreateVersionModal}
                    onVersionChange={(value) => {
                        if (/^[0-9.]*$/.test(value)) {
                            this.setState({ newVersion: value });
                        }
                    }}
                    onSubmit={this.handleCreateVersion}
                    isSubmitDisabled={!/^\d+\.\d+\.\d+$/.test(this.state.newVersion)}
                />
            </div >
        );
    }
}

export default StationAccordion;